import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Home, Briefcase, MapPin, Trash2, Loader2, Plus, X, Check } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";
import AddressInput from "@/shared/components/AddressInput";
import SubPageShell from "@/apps/rider/components/SubPageShell";

/**
 * /saved-places — manage Home + Work + custom labelled addresses.
 *
 * Backed by:
 *   PATCH  /api/auth/me              { home_address }     → sets Home
 *   GET    /api/auth/me/addresses                          → list
 *   POST   /api/auth/me/addresses    { label, address }    → add / replace by label
 *   DELETE /api/auth/me/addresses/:id                      → remove
 *
 * "Work" is just a labelled entry in address_book — first match
 * (case-insensitive) is treated as the canonical Work entry.
 */
export default function SavedPlaces() {
  const { user, refreshUser, loading: authLoading } = useAuth();
  const [book, setBook] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'home' | 'work' | 'custom' | null
  const [editValue, setEditValue] = useState("");
  const [editLabel, setEditLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const home = user?.home_address || "";
  const work = (book || []).find((a) => (a.label || "").trim().toLowerCase() === "work");
  const others = (book || []).filter((a) => (a.label || "").trim().toLowerCase() !== "work");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/me/addresses");
      setBook(Array.isArray(data) ? data : []);
    } catch (e) {
      // 401 in unauth flow — drop to signed-out state below.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const beginEdit = (kind, value = "", label = "") => {
    setEditing(kind);
    setEditValue(value);
    setEditLabel(label);
  };

  const cancelEdit = () => { setEditing(null); setEditValue(""); setEditLabel(""); };

  const saveHome = async () => {
    if (!editValue.trim()) return;
    setBusy(true);
    try {
      await api.patch("/auth/me", { home_address: editValue.trim() });
      await refreshUser?.();
      toast.success("Home saved");
      cancelEdit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save home");
    } finally {
      setBusy(false);
    }
  };

  const removeHome = async () => {
    if (!window.confirm("Remove your saved home address?")) return;
    setBusy(true);
    try {
      await api.patch("/auth/me", { home_address: "" });
      await refreshUser?.();
      toast.success("Home removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove");
    } finally {
      setBusy(false);
    }
  };

  const saveLabelled = async (label) => {
    const lbl = (label || editLabel).trim();
    const addr = editValue.trim();
    if (!lbl || !addr) {
      toast.error("Both label and address are required");
      return;
    }
    setBusy(true);
    try {
      // Replace strategy: backend doesn't dedupe by label; remove any
      // existing same-label entry first so we always have one canonical
      // "Work" / "Gym" / etc. entry.
      const dup = (book || []).find((a) => (a.label || "").trim().toLowerCase() === lbl.toLowerCase());
      if (dup) await api.delete(`/auth/me/addresses/${dup.id}`);
      const { data } = await api.post("/auth/me/addresses", { label: lbl, address: addr });
      setBook(data || []);
      toast.success(`${lbl} saved`);
      cancelEdit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const removeLabelled = async (entry) => {
    if (!window.confirm(`Remove "${entry.label}"?`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/auth/me/addresses/${entry.id}`);
      setBook(data || []);
      toast.success(`${entry.label} removed`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove");
    } finally {
      setBusy(false);
    }
  };

  if (authLoading) return null;
  if (!user) {
    return (
      <SubPageShell eyebrow="Places" title="Saved places" subtitle="Sign in to save Home, Work and your favorite spots."
        back="/account" backLabel="Account" testId="saved-places-page">
        <Link to="/login" className="inline-flex items-center gap-2 bg-[#0A1128] text-[#F9F7F4] px-5 py-3 rounded-xl text-[12px] tracking-[0.18em] uppercase font-semibold">
          Sign in
        </Link>
      </SubPageShell>
    );
  }

  return (
    <SubPageShell
      eyebrow="Places"
      title="Saved places"
      subtitle="Save Home, Work, and your regular destinations to skip the address typing."
      back="/account"
      backLabel="Account"
      testId="saved-places-page"
    >
      {loading ? (
        <div className="flex items-center gap-2 text-[12px] text-[#515E7B] py-12 justify-center">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-3" data-testid="saved-places-list">
          {/* HOME */}
          <PlaceCard
            Icon={Home}
            label="Home"
            value={home}
            editing={editing === "home"}
            onEdit={() => beginEdit("home", home)}
            onCancel={cancelEdit}
            onSave={saveHome}
            onRemove={home ? removeHome : null}
            value$={editValue}
            setValue={setEditValue}
            placeholder="Where do you live?"
            busy={busy}
            testId="saved-place-home"
          />
          {/* WORK */}
          <PlaceCard
            Icon={Briefcase}
            label="Work"
            value={work?.address || ""}
            editing={editing === "work"}
            onEdit={() => beginEdit("work", work?.address || "", "Work")}
            onCancel={cancelEdit}
            onSave={() => saveLabelled("Work")}
            onRemove={work ? () => removeLabelled(work) : null}
            value$={editValue}
            setValue={setEditValue}
            placeholder="Where do you head most days?"
            busy={busy}
            testId="saved-place-work"
          />

          {/* CUSTOM */}
          {others.map((entry) => (
            <PlaceCard
              key={entry.id}
              Icon={MapPin}
              label={entry.label}
              value={entry.address}
              editing={false}
              onRemove={() => removeLabelled(entry)}
              busy={busy}
              testId={`saved-place-${entry.id}`}
            />
          ))}

          {editing === "custom" ? (
            <div className="bg-white border hairline rounded-2xl p-4" data-testid="saved-places-custom-form">
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#ed6500] mb-2">New place</div>
              <input
                className="input-line w-full mb-3"
                placeholder="Label · Gym, Mom's, Studio…"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                maxLength={30}
                data-testid="saved-places-custom-label"
              />
              <AddressInput
                value={editValue}
                onChange={setEditValue}
                placeholder="Address"
                allowCurrentLocation
                showSavedPlaces={false}
                testId="saved-places-custom-input"
              />
              <div className="flex items-center gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => saveLabelled()}
                  disabled={busy || !editLabel.trim() || !editValue.trim()}
                  className="bg-[#0A1128] text-[#F9F7F4] px-4 py-2 rounded-lg text-[11px] tracking-[0.2em] uppercase font-semibold disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="saved-places-custom-save"
                >
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                  Save
                </button>
                <button type="button" onClick={cancelEdit} className="px-3 py-2 text-[11px] tracking-[0.2em] uppercase font-semibold text-[#515E7B] hover:text-[#ed6500]">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => beginEdit("custom", "", "")}
              className="w-full mt-1 bg-white border hairline rounded-2xl p-4 flex items-center gap-3 hover:border-[#ed6500]/50 transition-colors"
              data-testid="saved-places-add-custom"
            >
              <span className="w-10 h-10 inline-flex items-center justify-center bg-[#FCEADE] text-[#ed6500] rounded-xl shrink-0">
                <Plus size={16} />
              </span>
              <div className="flex-1 text-left">
                <div className="font-semibold text-[#0A1128] text-[15px]">Add a new place</div>
                <div className="text-xs text-[#515E7B] mt-0.5">Gym, school pickup, anywhere you go often</div>
              </div>
            </button>
          )}
        </div>
      )}
    </SubPageShell>
  );
}

function PlaceCard({
  Icon, label, value, editing, onEdit, onCancel, onSave, onRemove,
  value$, setValue, placeholder, busy, testId,
}) {
  return (
    <div className="bg-white border hairline rounded-2xl p-4" data-testid={testId}>
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 inline-flex items-center justify-center bg-[#FCEADE] text-[#ed6500] rounded-xl shrink-0">
          <Icon size={16} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B]">{label}</div>
          {editing ? (
            <div className="mt-2">
              <AddressInput
                value={value$}
                onChange={setValue}
                placeholder={placeholder}
                allowCurrentLocation
                showSavedPlaces={false}
                testId={`${testId}-input`}
              />
              <div className="flex items-center gap-2 mt-3">
                <button
                  type="button"
                  onClick={onSave}
                  disabled={busy || !value$.trim()}
                  className="bg-[#0A1128] text-[#F9F7F4] px-3 py-2 rounded-lg text-[11px] tracking-[0.2em] uppercase font-semibold disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid={`${testId}-save`}
                >
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                  Save
                </button>
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-3 py-2 text-[11px] tracking-[0.2em] uppercase font-semibold text-[#515E7B] hover:text-[#ed6500]"
                  data-testid={`${testId}-cancel`}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : value ? (
            <>
              <div className="text-[14.5px] text-[#0A1128] leading-snug mt-0.5 break-words">
                {value}
              </div>
              <div className="flex items-center gap-3 mt-2">
                <button
                  type="button"
                  onClick={onEdit}
                  className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#0A1128] hover:text-[#ed6500]"
                  data-testid={`${testId}-edit`}
                >
                  Edit
                </button>
                {onRemove && (
                  <button
                    type="button"
                    onClick={onRemove}
                    className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#515E7B] hover:text-[#ed6500] inline-flex items-center gap-1"
                    data-testid={`${testId}-remove`}
                  >
                    <Trash2 size={11} /> Remove
                  </button>
                )}
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={onEdit}
              className="text-[12px] text-[#515E7B] hover:text-[#ed6500] mt-1 inline-flex items-center gap-1"
              data-testid={`${testId}-add`}
            >
              <Plus size={11} /> Add {label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// keep X import for any future close-icon needs without unused-import lints
// eslint-disable-next-line no-unused-vars
const _kept = X;
